import axios from "axios";
import { toast } from "react-toastify";
import { getApiBase } from "../config/env";

const PLATFORM_SELECTED_TENANT_KEY_STORAGE = "platform_selected_tenant_key";
const PLATFORM_SELECTED_TENANT_ID_STORAGE = "platform_selected_tenant_id";
const PLATFORM_SELECTED_TENANT_NAME_STORAGE = "platform_selected_tenant_name";
const PLATFORM_SELECTED_TENANT_ORG_CODE_STORAGE = "platform_selected_tenant_org_code";
const AUTH_TENANT_KEY_STORAGE = "auth_tenant_key";
const AUTH_TENANT_ID_STORAGE = "auth_tenant_id";
const AUTH_TENANT_NAME_STORAGE = "auth_tenant_name";
const AUTH_TENANT_ORG_CODE_STORAGE = "auth_tenant_org_code";
const AUTH_PAYLOAD_VERSION_STORAGE = "auth_payload_version";
export const AUTH_PAYLOAD_VERSION = "2026-04-16-org-role-permissions";

const api = axios.create({
  baseURL: getApiBase(),
});

const setAuthHeader = (token) => {
  if (token) api.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete api.defaults.headers.common.Authorization;
};

export const getSelectedPlatformTenantContext = () => {
  const key = localStorage.getItem(PLATFORM_SELECTED_TENANT_KEY_STORAGE);
  if (!key) {
    return null;
  }

  return {
    key,
    id: localStorage.getItem(PLATFORM_SELECTED_TENANT_ID_STORAGE),
    name: localStorage.getItem(PLATFORM_SELECTED_TENANT_NAME_STORAGE),
    orgCode: localStorage.getItem(PLATFORM_SELECTED_TENANT_ORG_CODE_STORAGE),
  };
};

export const getAuthTenantContext = () => {
  const key = localStorage.getItem(AUTH_TENANT_KEY_STORAGE);
  if (!key) {
    return null;
  }

  return {
    key,
    id: localStorage.getItem(AUTH_TENANT_ID_STORAGE),
    name: localStorage.getItem(AUTH_TENANT_NAME_STORAGE),
    orgCode: localStorage.getItem(AUTH_TENANT_ORG_CODE_STORAGE),
  };
};

const setAuthTenantContext = (tenant) => {
  const source = tenant?.tenant || tenant;
  const key = String(source?.key || "").trim();
  if (!key) {
    clearAuthTenantContext();
    return;
  }

  localStorage.setItem(AUTH_TENANT_KEY_STORAGE, key);

  if (source?.id != null) {
    localStorage.setItem(AUTH_TENANT_ID_STORAGE, String(source.id));
  } else {
    localStorage.removeItem(AUTH_TENANT_ID_STORAGE);
  }

  if (source?.display_name || source?.name) {
    localStorage.setItem(AUTH_TENANT_NAME_STORAGE, String(source.display_name || source.name));
  } else {
    localStorage.removeItem(AUTH_TENANT_NAME_STORAGE);
  }

  if (source?.org_code || source?.orgCode) {
    localStorage.setItem(AUTH_TENANT_ORG_CODE_STORAGE, String(source.org_code || source.orgCode));
  } else {
    localStorage.removeItem(AUTH_TENANT_ORG_CODE_STORAGE);
  }
};

export const clearAuthTenantContext = () => {
  localStorage.removeItem(AUTH_TENANT_KEY_STORAGE);
  localStorage.removeItem(AUTH_TENANT_ID_STORAGE);
  localStorage.removeItem(AUTH_TENANT_NAME_STORAGE);
  localStorage.removeItem(AUTH_TENANT_ORG_CODE_STORAGE);
};

export const setSelectedPlatformTenantContext = (tenant) => {
  const key = String(tenant?.key || "").trim();
  if (!key) {
    clearSelectedPlatformTenantContext();
    return;
  }

  localStorage.setItem(PLATFORM_SELECTED_TENANT_KEY_STORAGE, key);

  if (tenant?.id != null) {
    localStorage.setItem(PLATFORM_SELECTED_TENANT_ID_STORAGE, String(tenant.id));
  } else {
    localStorage.removeItem(PLATFORM_SELECTED_TENANT_ID_STORAGE);
  }

  if (tenant?.display_name || tenant?.name) {
    localStorage.setItem(
      PLATFORM_SELECTED_TENANT_NAME_STORAGE,
      String(tenant.display_name || tenant.name)
    );
  } else {
    localStorage.removeItem(PLATFORM_SELECTED_TENANT_NAME_STORAGE);
  }

  if (tenant?.org_code || tenant?.orgCode) {
    localStorage.setItem(
      PLATFORM_SELECTED_TENANT_ORG_CODE_STORAGE,
      String(tenant.org_code || tenant.orgCode)
    );
  } else {
    localStorage.removeItem(PLATFORM_SELECTED_TENANT_ORG_CODE_STORAGE);
  }
};

export const clearSelectedPlatformTenantContext = () => {
  localStorage.removeItem(PLATFORM_SELECTED_TENANT_KEY_STORAGE);
  localStorage.removeItem(PLATFORM_SELECTED_TENANT_ID_STORAGE);
  localStorage.removeItem(PLATFORM_SELECTED_TENANT_NAME_STORAGE);
  localStorage.removeItem(PLATFORM_SELECTED_TENANT_ORG_CODE_STORAGE);
};

const normalizeRequestPath = (url) => {
  if (!url) return "";

  try {
    return new URL(url, api.defaults.baseURL).pathname.replace(/^\/+api/, "").replace(/^\/+/, "");
  } catch {
    return String(url).replace(/^\/+/, "");
  }
};

const removeHeader = (headers, name) => {
  if (!headers) {
    return;
  }

  if (typeof headers.delete === "function") {
    headers.delete(name);
    headers.delete(name.toLowerCase());
  }

  delete headers[name];
  delete headers[name.toLowerCase()];
};

const shouldAttachSelectedTenantHeader = (config) => {
  if (localStorage.getItem("is_platform_admin") !== "1") {
    return false;
  }

  const selectedTenant = getSelectedPlatformTenantContext();
  if (!selectedTenant?.key) {
    return false;
  }

  const path = normalizeRequestPath(config?.url);
  if (
    /^(login|refresh)(\/|$)/.test(path)
    || /^(platform)(\/|$)/.test(path)
    || /^(public)(\/|$)/.test(path)
    || /^(auth)(\/|$)/.test(path)
    || /^(password)(\/|$)/.test(path)
  ) {
    return false;
  }

  return true;
};

const shouldAttachAuthTenantHeader = (config) => {
  const authTenant = getAuthTenantContext();
  if (!authTenant?.key) {
    return false;
  }

  const path = normalizeRequestPath(config?.url);
  return !(
    /^(login)(\/|$)/.test(path)
    || /^(public)(\/|$)/.test(path)
    || /^(auth)(\/|$)/.test(path)
    || /^(password)(\/|$)/.test(path)
  );
};

export const clearAuthAndRedirect = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("token_expiry");
  localStorage.removeItem("token");
  localStorage.removeItem("role");
  localStorage.removeItem("actual_role");
  localStorage.removeItem("permissions");
  localStorage.removeItem("workflow_actions");
  localStorage.removeItem("is_platform_admin");
  localStorage.removeItem("email");
  localStorage.removeItem("name");
  localStorage.removeItem("employee_code");
  localStorage.removeItem("gender");
  localStorage.removeItem("pr");
  localStorage.removeItem("avatar_url");
  localStorage.removeItem(AUTH_PAYLOAD_VERSION_STORAGE);
  clearSelectedPlatformTenantContext();
  clearAuthTenantContext();
  setAuthHeader(null);
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

// ✅ Use backend response (expires_at/expires_in), NOT JWT decoding
export const saveAuthFromResponse = (data) => {
  const previousEmployeeCode = localStorage.getItem("employee_code");

  if (data?.access_token) {
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("token", data.access_token);
    setAuthHeader(data.access_token);
  }

  // ✅ IMPORTANT: refresh token is rotated by backend
  if (data?.refresh_token) {
    localStorage.setItem("refresh_token", data.refresh_token);
  }

  const effectiveRole = data?.effective_role || data?.role || data?.user?.role;
  if (effectiveRole) {
    localStorage.setItem("role", effectiveRole);
  } else {
    localStorage.removeItem("role");
  }
  if (data?.user?.actual_role) {
    localStorage.setItem("actual_role", data.user.actual_role);
  } else if (data?.actual_role) {
    localStorage.setItem("actual_role", data.actual_role);
  } else {
    localStorage.removeItem("actual_role");
  }

  if (Array.isArray(data?.permissions)) {
    localStorage.setItem("permissions", JSON.stringify(data.permissions));
  } else {
    localStorage.removeItem("permissions");
  }

  localStorage.setItem(AUTH_PAYLOAD_VERSION_STORAGE, AUTH_PAYLOAD_VERSION);

  if (Array.isArray(data?.workflow_actions)) {
    localStorage.setItem("workflow_actions", JSON.stringify(data.workflow_actions));
  } else {
    localStorage.removeItem("workflow_actions");
  }

  if (data?.user?.employee_code) {
    localStorage.setItem("employee_code", String(data.user.employee_code));
  } else {
    localStorage.removeItem("employee_code");
  }

  if (typeof data?.is_platform_admin === 'boolean') {
    localStorage.setItem('is_platform_admin', data.is_platform_admin ? '1' : '0');
  } else {
    localStorage.removeItem('is_platform_admin');
  }

  if (data?.tenant?.tenant || data?.tenant?.key) {
    setAuthTenantContext(data.tenant);
  } else if (data?.access_token) {
    clearAuthTenantContext();
  }

  const incomingEmployeeCode = data?.user?.employee_code != null
    ? String(data.user.employee_code)
    : null;
  const isPlatformAdmin = data?.is_platform_admin === true;

  if (!isPlatformAdmin || (incomingEmployeeCode && previousEmployeeCode && previousEmployeeCode !== incomingEmployeeCode)) {
    clearSelectedPlatformTenantContext();
  }

  if (data?.user?.email) {
    localStorage.setItem('email', String(data.user.email));
  } else {
    localStorage.removeItem('email');
  }

  if (data?.user?.name) {
    localStorage.setItem('name', String(data.user.name));
  } else {
    localStorage.removeItem('name');
  }

  if (data?.user?.gender != null) {
    localStorage.setItem('gender', String(data.user.gender));
  } else {
    localStorage.removeItem('gender');
  }

  if (data?.user?.pr != null) {
    localStorage.setItem('pr', String(data.user.pr));
  } else {
    localStorage.removeItem('pr');
  }

  if (data?.user?.avatar_url) {
    localStorage.setItem('avatar_url', String(data.user.avatar_url));
  } else {
    localStorage.removeItem('avatar_url');
  }

  const leewayMs = 30 * 1000;
  let expiryMs = null;

  if (data?.expires_at) {
    const t = new Date(data.expires_at).getTime();
    if (Number.isFinite(t)) expiryMs = t - leewayMs;
  } else if (data?.expires_in) {
    const sec = Number(data.expires_in);
    if (Number.isFinite(sec)) expiryMs = Date.now() + sec * 1000 - leewayMs;
  }

  if (expiryMs) {
    localStorage.setItem("token_expiry", String(expiryMs));
  } else {
    localStorage.removeItem("token_expiry");
  }

  return {
    isUserSwitch: Boolean(
      previousEmployeeCode &&
        incomingEmployeeCode &&
        previousEmployeeCode !== incomingEmployeeCode
    ),
    incomingEmployeeCode,
    previousEmployeeCode,
  };
};

// Attach token on every request if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['X-Client-Platform'] = 'web';
  if (shouldAttachAuthTenantHeader(config)) {
    const authTenant = getAuthTenantContext();
    config.headers['X-Auth-Tenant-Key'] = authTenant.key;
  } else {
    removeHeader(config.headers, 'X-Auth-Tenant-Key');
    removeHeader(config.headers, 'X-Auth-Tenant-Org-Code');
  }
  if (shouldAttachSelectedTenantHeader(config)) {
    const selectedTenant = getSelectedPlatformTenantContext();
    config.headers['X-Tenant-Key'] = selectedTenant.key;
  } else {
    removeHeader(config.headers, 'X-Tenant-Key');
    removeHeader(config.headers, 'X-Tenant-Org-Code');
  }
  return config;
});

setAuthHeader(localStorage.getItem("access_token"));

// ----- Refresh handling (safe + no global flag) -----
let isRefreshing = false;
let queue = [];

const runQueue = (error, token = null) => {
  queue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  queue = [];
};

api.interceptors.response.use(
  (response) => {
    // Keep auth storage synced on both /login and /refresh responses.
    if (response?.data?.access_token) {
      saveAuthFromResponse(response.data);
    }
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config;
    const requestUrl = String(original?.url || "");
const isAuthRequest = /\/(login|refresh)(\?|$)/.test(requestUrl);

    if (status === 403 && error?.response?.data?.message === 'The authenticated token is not valid for the resolved tenant.') {
      toast.error("Your session tenant changed. Please log in again.");
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    if ((status !== 401 && status !== 419) || !original) {
      if (status >= 500 && !original?.suppress500Toast) {
        toast.error("Internal server error. Please try again later.");
      } else if (status === 404 && !original?.suppress404Toast) {
        toast.error("The requested resource was not found.");
      }
      return Promise.reject(error);
    }

    // Never try to refresh auth for login/refresh endpoints.
    if (isAuthRequest || original?.skipAuthRefresh) {
      return Promise.reject(error);
    }

    // ✅ per-request retry flag (no global attemptedRefresh)
    if (original._retry) return Promise.reject(error);
    original._retry = true;

    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      toast.error("Your session has expired. Please log in again.");
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    // If another refresh in progress, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queue.push({
          resolve: (token) => {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(api(original));
          },
          reject,
        });
      });
    }

    isRefreshing = true;

    try {
      const refreshResp = await api.post(
        "/refresh",
        { refresh_token: refreshToken },
        { skipAuthRefresh: true }
      );

      // ✅ saves access_token + rotated refresh_token + expiry
      saveAuthFromResponse(refreshResp.data);

      const newAccessToken = refreshResp.data.access_token;
      if (!newAccessToken) throw new Error("Refresh succeeded but no access_token returned");

      runQueue(null, newAccessToken);

      original.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(original);
    } catch (refreshErr) {
      runQueue(refreshErr, null);
      console.error("Refresh token failed:", refreshErr);

      toast.error("Your session has expired. Please log in again.");
      clearAuthAndRedirect();
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

// ---------------------------------------------------------------------------
// Duplicate-submit guard (applies to the WHOLE app)
// ---------------------------------------------------------------------------
// Stops accidental double-clicks on action buttons (Approve / Reject / Save /
// Submit / Delete, etc.) from firing the same mutating request more than once.
//
//  * Identical POST/PUT/PATCH/DELETE calls that are still in flight share a
//    single network request — the 2nd (3rd, ...) click reuses the 1st promise,
//    so the server only ever processes the action once.
//  * A short trailing window after a SUCCESSFUL call also absorbs very fast
//    double-clicks where the first request already resolved.
//  * Failures are released immediately, so a user can retry after an error.
//  * GET requests are never deduplicated (reads stay independent).
//  * Pass { allowDuplicate: true } in the request config to opt a specific
//    call out of the guard (e.g. intentional rapid repeats).
const inFlightMutations = new Map();
const DEDUPE_SUCCESS_RETENTION_MS = 600;

const serializeRequestBody = (data) => {
  if (data == null) return "";
  if (typeof data === "string") return data;
  if (typeof FormData !== "undefined" && data instanceof FormData) return "formdata";
  if (typeof Blob !== "undefined" && data instanceof Blob) return "blob";
  try {
    return JSON.stringify(data);
  } catch {
    return "unserializable";
  }
};

const buildMutationKey = (method, url, body) =>
  `${method}|${String(url || "")}|${serializeRequestBody(body)}`;

const guardMutationMethod = (method, hasBodyArg) => {
  const original = api[method].bind(api);

  api[method] = (...args) => {
    const url = args[0];
    const body = hasBodyArg ? args[1] : undefined;
    const config = hasBodyArg ? args[2] : args[1];

    // Explicit opt-out for intentional repeats.
    if (config && config.allowDuplicate) {
      return original(...args);
    }

    const key = buildMutationKey(method, url, hasBodyArg ? body : config && config.data);

    const existing = inFlightMutations.get(key);
    if (existing) {
      return existing;
    }

    const promise = original(...args);
    inFlightMutations.set(key, promise);

    const clear = () => {
      if (inFlightMutations.get(key) === promise) {
        inFlightMutations.delete(key);
      }
    };

    // Keep the key briefly after success (absorbs fast double-clicks on quick
    // endpoints); release immediately on failure so retries are allowed.
    promise.then(
      () => setTimeout(clear, DEDUPE_SUCCESS_RETENTION_MS),
      () => clear()
    );

    return promise;
  };
};

guardMutationMethod("post", true);
guardMutationMethod("put", true);
guardMutationMethod("patch", true);
guardMutationMethod("delete", false);

export default api;
